import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '@/store';
import { MIYO_DISABLED_COLORS } from '../settings';
import { setView } from '../../../actions/pdp.actions';
import { PDPViews } from '@types';
import { formatCurrency } from '@utils/formatters.util';
import { useMemo } from 'react';
import { Markdown } from '@features/markdown/components/Markdown';
import { FaArrowRightLong } from 'react-icons/fa6';
import { Link } from '@components/Link';

const PersonalizeProduct = ({ className = '', onCustomize }) => {
  const dispatch = useAppDispatch();
  const { canMiyo, variants, details, canMiyoProduct, site, miyo } = useSelector(
    (state: any) => state.pdpItem
  );

  const [graphicTypes, canEngrave, canColor, processingDays, rushDays] = useMemo(() => {
    const config = miyo?.preview?.[0]?.miyoConfig?.graphicType || {};

    const graphicTypes = Object.keys(config).filter(
      (key) => window.DD_ENV === 'dev' || config[key] === 'prod'
    );

    const canEngrave = graphicTypes.includes('engraved');
    const canColor = graphicTypes.includes('color');

    const category = miyo?.preview?.[0]?.category;
    const flags = site?.flags;

    const processingDays = Math.max(
      ...graphicTypes.map((graphicType) => details.processing.customization[graphicType]?.normal ?? 0)
    );

    const rushProcessingDays = Math.max(
      ...graphicTypes.map((graphicType) => details.processing.customization[graphicType]?.rush ?? 0)
    );

    return [graphicTypes, canEngrave, canColor, processingDays, rushProcessingDays];
  }, [miyo, site]);

  const miyoOverride = details?.uom.id === 1;

  const rate = miyo?.faceFee?.rate;
  const usualRate = miyo?.faceFee?.usualRate;
  const discount = Math.round((1 - rate / usualRate) * 100);

  const isRushEnabled = !!site?.flags?.enableCustomizationRush && processingDays !== rushDays;

  // disable for now
  // if (false && canCustomize && !canMiyoProduct) {
  //   return <BulkPersonalizeProduct className={className} />;
  // }

  if (!miyoOverride || !canMiyoProduct) return null;

  const isMiyoable = miyoOverride && canMiyo && !MIYO_DISABLED_COLORS.includes(variants?.activeColor?.id);

  const _onCustomize = (e) => {
    if (typeof onCustomize === 'function') {
      if (isMiyoable) onCustomize(e);
    } else {
      e.preventDefault();
      if (isMiyoable) dispatch(setView(PDPViews.DESIGN));
    }
  };

  return (
    <div className={classNames('tw:mb-4', className)}>
      <Link
        onClick={_onCustomize}
        className='tw:flex tw:cursor-pointer tw:flex-col tw:justify-between tw:gap-4 tw:overflow-hidden tw:rounded-button tw:border tw:border-neutrals-light-grey tw:px-6 tw:py-3 tw:no-underline tw:transition tw:duration-200 tw:hover:bg-neutral-100'>
        <div className='tw:flex tw:w-full tw:items-center tw:justify-between tw:gap-4'>
          <div className='tw:text-left'>
            <p className='tw:mb-0 tw:inline-flex tw:flex-wrap tw:items-center tw:gap-1 tw:text-sm tw:font-normal tw:text-brand-black tw:md:text-base'>
              <span>{canEngrave ? 'Engrave' : 'Personalize'} with</span>{' '}
              <img
                src='/assets/images/rtic/myRTIC-Logo.png'
                alt='MyRTIC'
                className='tw:h-[14px] tw:w-full tw:max-w-[65px]'
              />
            </p>

            {rate >= 0 && (
              <p className='tw:mb-0 tw:mt-1 tw:text-xs tw:text-neutrals-dark-grey'>
                {rate ? (
                  <>
                    {usualRate && (
                      <span className='tw:mr-1 tw:font-bold tw:text-onsale'>{discount}% OFF</span>
                    )}
                    <span>
                      starting at{' '}
                      <b
                        className={classNames({
                          'tw:text-body': !usualRate,
                          'tw:text-onsale': usualRate,
                        })}>
                        {formatCurrency(rate)}
                      </b>{' '}
                    </span>
                  </>
                ) : (
                  <span>
                    <b
                      className={classNames({
                        'tw:text-body': !usualRate,
                        'tw:text-onsale': usualRate,
                      })}>
                      Free!
                    </b>
                  </span>
                )}{' '}
                {usualRate && <span className='tw:line-through'>{formatCurrency(usualRate)}</span>}
              </p>
            )}

            <p
              className={classNames(
                'tw:mb-0 tw:mt-1 tw:text-2xs tw:font-normal tw:leading-tight tw:md:text-[11px]',
                {
                  'tw:text-neutrals-dark-grey': isMiyoable,
                  'tw:text-patriot-red': !isMiyoable,
                }
              )}>
              {isMiyoable ? (
                <>
                  {(processingDays || 0) > 0 && (
                    <>
                      Please Allow up to {processingDays} {processingDays === 1 ? 'Day' : 'Days'} to Process
                      your Order
                    </>
                  )}
                </>
              ) : (
                <>Not available in {variants?.activeColor?.value}. Please select another Color.</>
              )}
            </p>
          </div>
          <FaArrowRightLong size={20} />
        </div>

        {isRushEnabled && (
          <div className='tw:-mx-6 tw:-mb-3 tw:bg-[#e6e7ea] tw:px-6 tw:py-3 tw:text-xs'>
            {site.flags.enableCustomizationRush[0].moduleMessaging && (
              <Markdown
                source={site.flags.enableCustomizationRush[0].moduleMessaging}
                placeholders={{
                  MyRTIC: () => (
                    <img
                      src='/assets/images/rtic/myRTIC-Logo.png'
                      alt='MyRTIC'
                      className='tw:inline-block tw:h-[14px] tw:max-w-[65px] tw:align-middle'
                    />
                  ),
                  processingDays: () => `${processingDays} business day${processingDays > 1 && 's'}`,
                }}
              />
            )}
          </div>
        )}
      </Link>
    </div>
  );
};

export default PersonalizeProduct;
